import { ETCHO_TENANT_ID } from '@shared/data/Tenants';
import type { TenantConfig } from './tenantConfig';

/**
 * The tenant's app logo URL, or null when the app should show Etcho's own
 * bundled artwork (no tenant resolved, the Etcho tenant itself, or a tenant
 * without an uploaded logo).
 */
export function tenantLogoUrl(config: TenantConfig | null): string | null {
    if (!config || config.tenantId === ETCHO_TENANT_ID) {
        return null;
    }

    return config.branding.logoUrl ?? null;
}

// True when a non-Etcho tenant resolved for this hostname — the signal for
// chrome (login page, masthead, tab) to drop Etcho-specific artwork/copy.
export function isBrandedTenant(config: TenantConfig | null): boolean {
    return !!config && config.tenantId !== ETCHO_TENANT_ID;
}

/**
 * Logo for dark surfaces (login page): the tenant's light variant when
 * uploaded, otherwise null — callers fall back to the tenant name in text.
 */
export function tenantLightLogoUrl(config: TenantConfig | null): string | null {
    if (!isBrandedTenant(config) || !config) {
        return null;
    }

    return config.branding.logoLightUrl ?? null;
}

/**
 * Stamp the browser-tab identity for a branded tenant: document title and
 * (when the tenant has one) the favicon. Etcho and unresolved hostnames keep
 * the index.html defaults ("Etcho Pro" + the bundled favicon).
 */
export function applyTenantDocumentIdentity(
    config: TenantConfig | null,
): void {
    if (!config || config.tenantId === ETCHO_TENANT_ID) {
        return;
    }

    document.title = config.name;

    if (config.branding.faviconUrl) {
        const link =
            document.querySelector<HTMLLinkElement>('link[rel="icon"]') ??
            (() => {
                const created = document.createElement('link');
                created.rel = 'icon';
                document.head.appendChild(created);
                return created;
            })();
        link.href = config.branding.faviconUrl;
    }
}
